import { Button, FileButton, Group, JsonInput, Select, Stack, Tabs, Text, TextInput } from "@mantine/core";
import { IconSparkles, IconUpload } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useGeoLocations } from "shared/components/organization/geolocations/useGeoLocations";
import { catching, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { geozod } from "shared/utils/zod";

function normalizeGeoJsonBoundary(boundary: string | null | undefined): string {
    if (!boundary?.trim()) return "";
    try {
        return JSON.stringify(JSON.parse(boundary), null, 2);
    } catch {
        return boundary;
    }
}

export const AddShapeForm = () => {
    const geo = useGeoLocations();
    const [editingShapeName, setEditingShapeName] = useState("");
    const [jsonInput, setJsonInput] = useState("");
    const resetRef = useRef<() => void>(null); // in case user uploads again

    useEffect(() => setEditingShapeName(geo.editingShape ? geo.editingShape.name : ""), [geo.editingShape]);
    useEffect(() => setJsonInput(geo.polygon ? JSON.stringify(geo.polygon, null, 2) : ""), [geo.polygon]);

    const originalLayer = geo.editingShape ? geo.layers.find((l) => l.filters?.some((f) => f.id === geo.editingShape?.id)) : null;
    const originalBoundary = normalizeGeoJsonBoundary(geo.editingShape?.boundary);
    const hasInput = (!!geo.polygon || !!jsonInput.trim()) && !!editingShapeName.trim();
    const hasChanges =
        !geo.editingShape ||
        editingShapeName.trim() !== geo.editingShape.name ||
        jsonInput.trim() !== originalBoundary.trim() ||
        geo.drawingLayerId !== originalLayer?.id;
    const isDirty = hasInput && hasChanges;

    function handleSave() {
        const name = editingShapeName.trim();
        if (!name || !jsonInput.trim()) return;

        const polygon = catching(() => geozod.polygon().parse(JSON.parse(jsonInput)));
        if (!polygon) {
            notify.catch("Invalid GeoJSON Polygon");
            return;
        }

        geo.setPolygon(polygon);
        geo.handleSaveShape(name, polygon);
    }

    function handleFileUpload(file: File | null) {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            setJsonInput(content);
            resetRef.current?.();
        };
        reader.readAsText(file);
    }

    return (
        <Stack p="md">
            <Select
                label="Layer"
                required
                data={geo.layers.map((l) => ({ value: l.id, label: l.name }))}
                value={geo.drawingLayerId}
                onChange={(value) => value && geo.setDrawingLayerId(value)}
                disabled={geo.drawingLayerLocked}
            />
            <TextInput
                label="Name"
                required
                placeholder="Enter Shape Name"
                value={editingShapeName}
                onChange={(event) => setEditingShapeName(event.currentTarget.value)}
            />
            <Tabs defaultValue="Map">
                <Tabs.List>
                    <Tabs.Tab value="Map">Map</Tabs.Tab>
                    <Tabs.Tab value="GeoJSON">Geo-Json</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="Map">
                    <Group gap="xs" mt="sm">
                        <Text size="sm">
                            Add shape directly on the map by clicking on the tool on the top left of the map
                            <span
                                className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon"
                                title="Polygon tool"
                                style={{
                                    display: "inline-block",
                                    width: "24px",
                                    height: "24px",
                                    verticalAlign: "middle",
                                    border: "none",
                                }}
                            />
                        </Text>
                    </Group>
                </Tabs.Panel>
                <Tabs.Panel value="GeoJSON">
                    <JsonInput
                        label="Geo-Json"
                        required
                        placeholder='{ "type": "Polygon", ...'
                        value={jsonInput}
                        onChange={setJsonInput}
                        formatOnBlur
                        autosize
                        minRows={6}
                        maxRows={12}
                        mt="xs"
                        error={maybe(catching(() => JSON.parse(jsonInput)))?.take((obj) =>
                            geozod.polygon().safeParse(obj).error?.errors.map(to("message")).join("\n"),
                        )}
                    />
                    <Group justify="space-between" mt="xs">
                        <FileButton resetRef={resetRef} onChange={handleFileUpload} accept="application/json,.geojson">
                            {(props) => (
                                <Button {...props} variant="default" size="xs" leftSection={<IconUpload size={16} />}>
                                    Upload File
                                </Button>
                            )}
                        </FileButton>
                        <Button
                            variant="transparent"
                            size="xs"
                            leftSection={<IconSparkles size={16} />}
                            onClick={() => {
                                catching(() => setJsonInput(JSON.stringify(JSON.parse(jsonInput), null, 2)));
                            }}
                            disabled={!jsonInput.trim()}
                        >
                            Format
                        </Button>
                    </Group>
                </Tabs.Panel>
            </Tabs>
            <Group display="flex" justify="flex-end" mt="md" gap="xs">
                <Button variant="default" onClick={geo.resetDrawingState} disabled={geo.isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} disabled={!isDirty} loading={geo.isSaving}>
                    {geo.editingShape ? "Update Shape" : "Add Shape"}
                </Button>
            </Group>
        </Stack>
    );
};
